import { useMemo, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ClipboardText,
  CookingPot,
  Monitor,
  SquaresFour,
  BellRinging,
  ForkKnife,
  ChartBar,
  ChartLine,
  Users,
  Storefront,
  CalendarCheck,
  Buildings,
  ArrowsLeftRight,
  CaretDown,
  CaretLeft,
  CaretRight,
  X,
  SignOut,
  DeviceTablet,
  Desktop,
  Star,
  QrCode,
  CreditCard,
  Tag,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import useAuthStore from '@stores/authStore';
import { useNotificationCounts } from '@hooks/useNotificationCounts';


const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 72;
const SECTIONS_STORAGE_KEY = 'sidebar-sections';
const DEFAULT_COLLAPSED = new Set([
  'nav.menuContent',
  'nav.analytics',
  'nav.administration',
  'nav.venueSection',
  'nav.platform',
]);

function loadCollapsedSections() {
  try {
    const stored = localStorage.getItem(SECTIONS_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {
    /* ignore */
  }
  return new Set(DEFAULT_COLLAPSED);
}

function saveCollapsedSections(set) {
  try {
    localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function groupVisibleItems(items) {
  const groups = [];
  let current = null;

  for (const item of items) {
    if (item.section) {
      current = { sectionKey: item.section, items: [] };
      groups.push(current);
    } else if (item.divider) {
      groups.push({ divider: true });
      current = null;
    } else if (current) {
      current.items.push(item);
    }
  }

  return groups;
}

const NAV_ITEMS = [
  // --- Operations ---
  { section: 'nav.operations' },
  {
    key: 'dashboard',
    label: 'nav.dashboard',
    icon: ChartBar,
    path: '/dashboard',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
  },
  {
    key: 'orders',
    label: 'nav.orders',
    icon: ClipboardText,
    path: '/orders',
    roles: ['WAITER', 'CASHIER', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'kitchen',
    label: 'nav.kitchen',
    icon: CookingPot,
    path: '/kitchen-display',
    roles: ['KITCHEN_STAFF', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'pickup',
    label: 'nav.pickupScreen',
    icon: Monitor,
    path: '/pickup-display',
    roles: ['WAITER', 'KITCHEN_STAFF', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },

  // --- Service ---
  { section: 'nav.service' },
  {
    key: 'tables',
    label: 'nav.tables',
    icon: SquaresFour,
    path: '/tables',
    roles: ['WAITER', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'reservations',
    label: 'nav.reservations',
    icon: CalendarCheck,
    path: '/reservations',
    roles: ['WAITER', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'serviceRequests',
    label: 'nav.serviceRequests',
    icon: BellRinging,
    path: '/service-requests',
    roles: ['WAITER', 'MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },

  // --- Ordering Channels ---
  { section: 'nav.quickAccess' },
  {
    key: 'kiosk',
    label: 'nav.kiosk',
    icon: DeviceTablet,
    path: '/kiosk',
    roles: ['WAITER', 'MANAGER', 'ADMIN'],
  },
  {
    key: 'counter',
    label: 'nav.counter',
    icon: Desktop,
    path: '/tabletview',
    roles: ['WAITER', 'MANAGER', 'ADMIN'],
  },

  // --- Menu & Content ---
  { section: 'nav.menuContent' },
  {
    key: 'menu',
    label: 'nav.menu',
    icon: ForkKnife,
    path: '/menue',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },

  // --- Analytics ---
  { section: 'nav.analytics' },
  {
    key: 'reports',
    label: 'nav.reports',
    icon: ChartLine,
    path: '/auswertungen',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'payments',
    label: 'nav.payments',
    icon: CreditCard,
    path: '/payments',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'ratings',
    label: 'nav.ratings',
    icon: Star,
    path: '/ratings',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },

  // --- Administration ---
  { section: 'nav.administration' },
  {
    key: 'personal',
    label: 'nav.personal',
    icon: Users,
    path: '/usermanagement',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'restaurantSettings',
    label: 'nav.restaurantSettings',
    icon: Storefront,
    path: '/restaurant-settings',
    roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
    requiresRestaurant: true,
  },
  {
    key: 'restaurantTypes',
    label: 'nav.restaurantTypes',
    icon: Tag,
    path: '/restaurant-types',
    roles: ['SYSTEM'],
    requiresRestaurant: false,
  },

  // --- Venue (visible to venue members) ---
  { section: 'nav.venueSection' },
  {
    key: 'myVenue',
    label: 'nav.myVenue',
    icon: Buildings,
    path: '/venue',
    requiresVenueMembership: true,
  },
  {
    key: 'venueQrCodes',
    label: 'nav.venueQrCodes',
    icon: QrCode,
    path: '/venue-qr-codes',
    requiresVenueMembership: true,
  },

  // --- Platform (SYSTEM only, no restaurant required) ---
  { section: 'nav.platform' },
  {
    key: 'venues',
    label: 'nav.venues',
    icon: Buildings,
    path: '/venues',
    roles: ['SYSTEM'],
  },
  {
    key: 'restaurants',
    label: 'nav.restaurants',
    icon: Storefront,
    path: '/restaurants',
    roles: ['SYSTEM'],
  },
  {
    key: 'userManagement',
    label: 'nav.userManagement',
    icon: Users,
    path: '/users',
    roles: ['SYSTEM'],
  },

];

// ── Venue sidebar items (venue-mode only) ─────────────────────────────────────
function getVenueNavItems(venueId) {
  return [
    { section: 'nav.operations' },
    { key: 'venueDashboard',   label: 'nav.dashboard',    icon: ChartBar,      path: `/venue/${venueId}/dashboard` },
    { key: 'venueOrders',      label: 'nav.orders',       icon: ClipboardText, path: `/venue/${venueId}/orders` },
    { section: 'nav.quickAccess' },
    { key: 'venuePickup',      label: 'nav.pickupScreen', icon: Monitor,       path: `/venue-pickup/${venueId}` },
    { key: 'venueKiosk',       label: 'nav.kiosk',        icon: DeviceTablet,  path: '/venue-kiosk' },
    { section: 'nav.administration' },
    { key: 'venueRestaurants', label: 'nav.restaurants',  icon: Storefront,    path: `/venue/${venueId}/restaurants` },
    { key: 'venueStaff',       label: 'nav.personal',     icon: Users,         path: `/venue/${venueId}/staff` },
    { key: 'venueQrCodes',     label: 'nav.venueQrCodes', icon: QrCode,        path: `/venue/${venueId}/qr-codes` },
    { key: 'venueSettings',    label: 'nav.myVenue',      icon: Buildings,     path: `/venue/${venueId}/settings` },
  ];
}

// ── Restaurant sidebar items when entered from venue context ──────────────────
function getRestaurantFromVenueNavItems(restaurantId) {
  return [
    { section: 'nav.operations' },
    { key: 'rfvDashboard', label: 'nav.dashboard',         icon: ChartBar,      path: `/restaurant/${restaurantId}/dashboard` },
    { key: 'rfvOrders',    label: 'nav.orders',            icon: ClipboardText, path: `/restaurant/${restaurantId}/orders` },
    { section: 'nav.menuContent' },
    { key: 'rfvMenu',      label: 'nav.menu',              icon: ForkKnife,     path: `/restaurant/${restaurantId}/menu` },
    { section: 'nav.service' },
    { key: 'rfvTables',    label: 'nav.tables',            icon: SquaresFour,   path: `/restaurant/${restaurantId}/tables` },
    { section: 'nav.administration' },
    { key: 'rfvStaff',     label: 'nav.personal',          icon: Users,         path: `/restaurant/${restaurantId}/staff` },
    { key: 'rfvQrCodes',   label: 'nav.qrCodes',           icon: QrCode,        path: `/restaurant/${restaurantId}/qr-codes` },
    { key: 'rfvSettings',  label: 'nav.restaurantSettings', icon: Storefront,   path: `/restaurant/${restaurantId}/settings` },
  ];
}

function isItemVisible(item, userRole, restaurantId, hasVenueMembership) {
  if (item.requiresVenueMembership) return hasVenueMembership && userRole !== 'SYSTEM';
  if (!item.roles.includes(userRole)) return false;
  if (item.requiresRestaurant && !restaurantId) return false;
  return true;
}

function getVisibleNavItems(userRole, restaurantId, hasVenueMembership) {
  const result = [];
  let currentSection = null;
  let sectionItems = [];

  for (const item of NAV_ITEMS) {
    if (item.section) {
      // Flush previous section if it had visible items
      if (currentSection && sectionItems.length > 0) {
        result.push(currentSection);
        result.push(...sectionItems);
      }
      currentSection = item;
      sectionItems = [];
    } else if (item.divider) {
      // Flush previous section, start a new group with no header
      if (currentSection && sectionItems.length > 0) {
        result.push(currentSection);
        result.push(...sectionItems);
      }
      currentSection = item;
      sectionItems = [];
    } else if (isItemVisible(item, userRole, restaurantId, hasVenueMembership)) {
      sectionItems.push(item);
    }
  }

  // Flush last section
  if (currentSection && sectionItems.length > 0) {
    result.push(currentSection);
    result.push(...sectionItems);
  }

  return result;
}

export default function Sidebar({ open, onClose, onToggle, topOffset = 0 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const user = useAuthStore((s) => s.user);
  const restaurantId = useAuthStore((s) => s.restaurantId);
  const venueId = useAuthStore((s) => s.venueId);
  const logout = useAuthStore((s) => s.logout);
  const memberships = useAuthStore((s) => s.memberships);
  const counts = useNotificationCounts();
  const userRole = useAuthStore((s) => s.getRole()) || 'CUSTOMER';
  const hasVenueMembership = useAuthStore((s) => s.hasVenueMembership());

  const isVenueMode = !!venueId && !restaurantId && userRole !== 'SYSTEM';
  const isRestaurantFromVenue = !!venueId && !!restaurantId && userRole !== 'SYSTEM';

  const visibleItems = useMemo(() => {
    if (isVenueMode) return getVenueNavItems(venueId);
    if (isRestaurantFromVenue) return getRestaurantFromVenueNavItems(restaurantId);
    return getVisibleNavItems(userRole, restaurantId, hasVenueMembership);
  }, [isVenueMode, isRestaurantFromVenue, venueId, restaurantId, userRole, hasVenueMembership]);

  const [collapsedSections, setCollapsedSections] = useState(
    loadCollapsedSections,
  );

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      saveCollapsedSections(next);
      return next;
    });
  }, []);

  const groups = useMemo(
    () => groupVisibleItems(visibleItems),
    [visibleItems],
  );

  const restaurantMembershipCount = useMemo(
    () => memberships.filter((m) => m.scopeType === 'RESTAURANT' && m.isActive).length,
    [memberships],
  );
  const venueMembershipCount = useMemo(
    () => memberships.filter((m) => m.scopeType === 'VENUE' && m.isActive).length,
    [memberships],
  );

  // Auto-expand section containing the active route
  const activeSectionKey = useMemo(() => {
    for (const group of groups) {
      if (group.divider) continue;
      for (const item of group.items) {
        if (
          location.pathname === item.path ||
          location.pathname.startsWith(item.path + '/')
        ) {
          return group.sectionKey;
        }
      }
    }
    return null;
  }, [location.pathname, groups]);

  useEffect(() => {
    if (!activeSectionKey) return;
    setCollapsedSections((prev) => {
      if (!prev.has(activeSectionKey)) return prev;
      const next = new Set(prev);
      next.delete(activeSectionKey);
      saveCollapsedSections(next);
      return next;
    });
  }, [activeSectionKey]);

  const collapsed = !open && !isMobile;
  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onClose?.();
    }
  };

  const handleLogout = () => {
    logout?.();
    navigate('/login');
  };

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header — collapse/close button only (logo lives in TopBar) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-end' : collapsed ? 'center' : 'flex-end',
          px: collapsed ? 1 : 2,
          py: 1.5,
          minHeight: 48,
        }}
      >
        {isMobile ? (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={t('common:app.closeNavigation', 'Navigation schließen')}
          >
            <X size={22} />
          </IconButton>
        ) : (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              transform: collapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          >
            <CaretLeft size={20} />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {groups.map((group, groupIdx) => {
          if (group.divider) {
            return <Divider key="divider" sx={{ my: 1 }} />;
          }

          const { sectionKey, items } = group;
          const isSectionCollapsed = collapsedSections.has(sectionKey);

          return (
            <Box key={sectionKey}>
              {/* Section header — clickable when sidebar is expanded */}
              {!collapsed && (
                <Box
                  onClick={() => toggleSection(sectionKey)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    mt: 0.5,
                    mx: 1,
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: 1,
                    bgcolor: 'rgba(var(--mui-palette-text-primaryChannel) / 0.04)',
                    borderTop: groupIdx > 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'rgba(var(--mui-palette-text-primaryChannel) / 0.08)' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontSize: '0.68rem',
                    }}
                  >
                    {t(`common:${sectionKey}`)}
                  </Typography>
                  {isSectionCollapsed ? (
                    <CaretRight
                      size={14}
                      style={{
                        color: 'var(--mui-palette-text-secondary)',
                      }}
                    />
                  ) : (
                    <CaretDown
                      size={14}
                      style={{
                        color: 'var(--mui-palette-text-secondary)',
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Items — always visible in icon mode, collapsible otherwise */}
              <Collapse
                in={collapsed || !isSectionCollapsed}
                timeout="auto"
              >
                {items.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;

                  return (
                    <List key={item.key} disablePadding>
                      <Tooltip
                        title={
                          collapsed ? t(`common:${item.label}`) : ''
                        }
                        placement="right"
                        arrow
                      >
                        <ListItem disablePadding sx={{ px: 1 }}>
                          <ListItemButton
                            onClick={() => handleNavigate(item.path)}
                            sx={{
                              minHeight: 44,
                              borderRadius: 1.5,
                              px: collapsed ? 1.5 : 2,
                              justifyContent: collapsed
                                ? 'center'
                                : 'flex-start',
                              borderLeft: active
                                ? `3px solid ${theme.palette.primary.main}`
                                : '3px solid transparent',
                              bgcolor: active
                                ? `${theme.palette.primary.main}14`
                                : 'transparent',
                              color: active
                                ? 'primary.main'
                                : 'text.secondary',
                              '&:hover': {
                                bgcolor: active
                                  ? `${theme.palette.primary.main}1F`
                                  : 'action.hover',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: collapsed ? 0 : 40,
                                justifyContent: 'center',
                                color: 'inherit',
                              }}
                            >
                              {item.key === 'orders' && (
                                <Badge badgeContent={counts.orders} color="error" invisible={counts.orders === 0}>
                                  <Icon
                                    size={22}
                                    weight={active ? 'fill' : 'regular'}
                                  />
                                </Badge>
                              )}
                              {item.key === 'serviceRequests' && (
                                <Badge badgeContent={counts.serviceRequests} color="error" invisible={counts.serviceRequests === 0}>
                                  <Icon
                                    size={22}
                                    weight={active ? 'fill' : 'regular'}
                                  />
                                </Badge>
                              )}
                              {item.key !== 'orders' && item.key !== 'serviceRequests' && (
                                <Icon
                                  size={22}
                                  weight={active ? 'fill' : 'regular'}
                                />
                              )}
                            </ListItemIcon>
                            {!collapsed && (
                              <ListItemText
                                primary={t(`common:${item.label}`)}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  fontWeight: active ? 600 : 400,
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      </Tooltip>
                    </List>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Divider />

      {/* User card */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 1.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        {!collapsed && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                'Benutzer'}
            </Typography>
            <Chip
              label={t(`common:roles.${userRole}`)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: `${theme.palette.primary.main}14`,
                color: 'primary.main',
              }}
            />
          </Box>
        )}
        {!collapsed && ((restaurantMembershipCount + venueMembershipCount) > 1 || (!!venueId && !!restaurantId)) && (
          <Tooltip title={t('common:nav.switchWorkspace', 'Arbeitsbereich wechseln')} placement="top">
            <IconButton
              onClick={() => navigate('/select-workspace')}
              size="small"
              sx={{ color: 'text.secondary' }}
              aria-label={t('common:nav.switchWorkspace', 'Arbeitsbereich wechseln')}
            >
              <ArrowsLeftRight size={18} />
            </IconButton>
          </Tooltip>
        )}
        {!collapsed && (
          <IconButton
            onClick={handleLogout}
            size="small"
            sx={{ color: 'text.secondary' }}
            aria-label={t('common:nav.logout')}
          >
            <SignOut size={20} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH_EXPANDED,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
          top: 72 + topOffset,
          height: `calc(100% - ${72 + topOffset}px)`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
